/**
 * Parse API / DRF error payloads into a user-friendly message.
 */

type ErrorValue = string | { string?: string; message?: string } | unknown;

type ApiErrorData = {
  message?: string;
  errors?: Record<string, ErrorValue | ErrorValue[]>;
  detail?: string | Record<string, ErrorValue | ErrorValue[]>;
  non_field_errors?: ErrorValue[];
  phone_number?: ErrorValue[];
  password?: ErrorValue[];
  email?: ErrorValue[];
};

const PYTHON_REPR_PATTERN = /ErrorDetail\(string='([^']*)'/;

function stringifyErrorValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const obj = value as { string?: string; message?: string };
    if (typeof obj.string === 'string') return obj.string;
    if (typeof obj.message === 'string') return obj.message;
  }

  const text = String(value);
  const match = text.match(PYTHON_REPR_PATTERN);
  if (match) return match[1];

  return text;
}

function extractFromErrorObject(data: Record<string, unknown>): string | null {
  const nonField = data.non_field_errors;
  if (Array.isArray(nonField) && nonField.length > 0) {
    return stringifyErrorValue(nonField[0]);
  }

  const detail = data.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (detail && typeof detail === 'object') {
    const nested = extractFromErrorObject(detail as Record<string, unknown>);
    if (nested) return nested;
  }

  for (const [key, value] of Object.entries(data)) {
    if (['success', 'message', 'errors', 'status_code'].includes(key)) continue;
    if (Array.isArray(value) && value.length > 0) {
      return stringifyErrorValue(value[0]);
    }
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return null;
}

function looksLikePythonRepr(message: string): boolean {
  return (
    message.includes('ErrorDetail(') ||
    (message.startsWith('{') && message.includes('non_field_errors'))
  );
}

function parsePythonReprMessage(message: string): string | null {
  const match = message.match(PYTHON_REPR_PATTERN);
  if (match) return match[1];

  const quotedMatch = message.match(/non_field_errors['"]:\s*\[[^\]]*['"]([^'"]+)['"]/);
  if (quotedMatch) return quotedMatch[1];

  return null;
}

export function parseApiError(err: unknown, fallback = 'Something went wrong'): string {
  const error = err as { data?: ApiErrorData; message?: string };
  const data = error?.data;

  if (data) {
    if (data.errors && typeof data.errors === 'object') {
      const fromErrors = extractFromErrorObject(data.errors as Record<string, unknown>);
      if (fromErrors) return fromErrors;
    }

    const fromData = extractFromErrorObject(data as Record<string, unknown>);
    if (fromData) return fromData;

    if (data.message) {
      if (looksLikePythonRepr(data.message)) {
        const parsed = parsePythonReprMessage(data.message);
        if (parsed) return parsed;
      } else if (data.message !== 'Validation failed' && data.message !== 'An error occurred') {
        return data.message;
      }
    }
  }

  if (error?.message && !looksLikePythonRepr(error.message)) {
    return error.message;
  }

  return fallback;
}
